import { INTRANET_AI_INSTRUCTION_MAX, PermissionCode } from '@gb-ticket/shared-types';
import { Loader2Icon } from 'lucide-react';
import { useState } from 'react';

import { KiMark } from '@/components/ki-mark';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ApiRequestError } from '@/lib/api';
import { hasPermission } from '@/platform/auth/permissions';
import { useMe } from '@/platform/auth/use-me';
import { AiLiveReply } from '@/platform/components/ai-live-reply';
import { IntranetBody } from '@/products/intranet/components/intranet-body';
import {
  useDraftIntranetAi,
  useIntranetAssistant,
} from '@/products/intranet/features/intranet/use-intranet-ai';

const REVIEW_HINT = 'Vorschlag prüfen. Veröffentlicht wird erst über den bisherigen Knopf.';
const OFFLINE_HINT = 'Die KI ist gerade nicht erreichbar. Der Editor bleibt unverändert.';

type Suggestion = {
  title?: string;
  teaser?: string;
  bodyHtml?: string;
};

export function IntranetAiDraftButton({
  title,
  teaser = '',
  bodyHtml,
  onApply,
}: {
  title: string;
  teaser?: string;
  bodyHtml: string;
  onApply: (suggestion: Suggestion) => void;
}) {
  const me = useMe();
  const assistant = useIntranetAssistant();
  const draft = useDraftIntranetAi();
  const [open, setOpen] = useState(false);
  const [instruction, setInstruction] = useState('');
  const [suggestion, setSuggestion] = useState<Suggestion | null>(null);
  const [offline, setOffline] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const visible =
    hasPermission(me.data, PermissionCode.INTRANET_CONTENT_WRITE) &&
    assistant.data?.intranetAssistant === true;
  if (!visible) {
    return null;
  }

  function reset() {
    setInstruction('');
    setSuggestion(null);
    setOffline(false);
    setError(null);
  }

  async function requestDraft() {
    const text = instruction.trim();
    if (!text) {
      setError('Bitte eine Anweisung eingeben.');
      return;
    }
    setError(null);
    setOffline(false);
    setSuggestion(null);
    try {
      const result = await draft.mutateAsync({
        instruction: text,
        ...(title.trim() ? { title: title.trim() } : {}),
        ...(teaser.trim() ? { teaser: teaser.trim() } : {}),
        ...(bodyHtml.trim() ? { bodyHtml } : {}),
      });
      const next: Suggestion = {
        ...(result.title ? { title: result.title } : {}),
        ...(result.teaser ? { teaser: result.teaser } : {}),
        ...(result.bodyHtml ? { bodyHtml: result.bodyHtml } : {}),
      };
      if (!result.chatAvailable || (!next.title && !next.teaser && !next.bodyHtml)) {
        setOffline(true);
        return;
      }
      setSuggestion(next);
    } catch (caught) {
      setError(
        caught instanceof ApiRequestError
          ? caught.message
          : 'Der Vorschlag konnte nicht erstellt werden.',
      );
    }
  }

  function apply() {
    if (!suggestion) {
      return;
    }
    onApply(suggestion);
    setOpen(false);
    reset();
  }

  return (
    <>
      <Button type="button" variant="outline" size="sm" onClick={() => setOpen(true)}>
        <KiMark />
        Mit KI formulieren
      </Button>
      <Dialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) {
            reset();
          }
        }}
      >
        <DialogContent className="max-h-[min(40rem,85vh)] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KiMark className="size-5" />
              Mit KI formulieren
            </DialogTitle>
            <DialogDescription>{REVIEW_HINT}</DialogDescription>
          </DialogHeader>
          <ContributionSnapshot title={title} teaser={teaser} bodyHtml={bodyHtml} />
          <div className="space-y-2">
            <Label htmlFor="intranet-ai-instruction">Anweisung</Label>
            <Textarea
              id="intranet-ai-instruction"
              value={instruction}
              maxLength={INTRANET_AI_INSTRUCTION_MAX}
              rows={4}
              onChange={(event) => setInstruction(event.target.value)}
            />
          </div>
          {offline ? (
            <p role="status" className="text-sm text-muted-foreground">
              {OFFLINE_HINT}
            </p>
          ) : null}
          {draft.isPending ? <AiLiveReply active text="" /> : null}
          {suggestion ? <SuggestionPreview suggestion={suggestion} /> : null}
          {error ? (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          ) : null}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={draft.isPending}
              onClick={() => void requestDraft()}
            >
              {draft.isPending ? <Loader2Icon className="animate-spin" aria-hidden /> : null}
              Vorschlag holen
            </Button>
            <Button type="button" disabled={!suggestion} onClick={apply}>
              Übernehmen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function ContributionSnapshot({
  title,
  teaser,
  bodyHtml,
}: {
  title: string;
  teaser: string;
  bodyHtml: string;
}) {
  const hasTitle = title.trim().length > 0;
  const hasTeaser = teaser.trim().length > 0;
  const hasBody = bodyHtml.trim().length > 0;
  if (!hasTitle && !hasTeaser && !hasBody) {
    return (
      <p className="text-sm text-muted-foreground">
        Im Beitrag steht noch kein Text. Die KI formuliert nur aus der Anweisung.
      </p>
    );
  }
  return (
    <section
      className="space-y-2 rounded-xl border border-border bg-card p-3 text-card-foreground shadow-sm"
      aria-label="Bisheriger Beitrag"
    >
      <p className="text-sm font-medium">Aus dem Beitrag</p>
      <p className="text-sm text-muted-foreground">
        Titel, Kurzfassung und Text werden automatisch mitformuliert.
      </p>
      {hasTitle ? <Field label="Titel" text={title} /> : null}
      {hasTeaser ? <Field label="Kurzfassung" text={teaser} /> : null}
      {hasBody ? (
        <div className="max-h-40 overflow-y-auto">
          <IntranetBody body={bodyHtml} />
        </div>
      ) : null}
    </section>
  );
}

function SuggestionPreview({ suggestion }: { suggestion: Suggestion }) {
  return (
    <section
      className="space-y-2 rounded-xl border border-border bg-card p-3 text-card-foreground shadow-sm"
      aria-label="Vorschlag der KI"
    >
      <p className="flex items-center gap-2 text-sm font-medium">
        <KiMark />
        Vorschlag der KI
      </p>
      {suggestion.title ? <Field label="Titel" text={suggestion.title} /> : null}
      {suggestion.teaser ? <Field label="Kurzfassung" text={suggestion.teaser} /> : null}
      {suggestion.bodyHtml ? (
        <div className="max-h-64 overflow-y-auto">
          <IntranetBody body={suggestion.bodyHtml} />
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">Zum Text gibt es keinen neuen Vorschlag.</p>
      )}
    </section>
  );
}

function Field({ label, text }: { label: string; text: string }) {
  return (
    <p className="text-sm whitespace-pre-wrap">
      <span className="text-muted-foreground">{label}: </span>
      {text}
    </p>
  );
}
